import React, { useEffect, useRef } from "react";
import Layout from "../../../components/admin/Layout";
import Helmet from "react-helmet";
import Table from "../../../components/ui/Table";
import usePaginate from "../../../hooks/api/usePaginate";
import apiRequest from "../../../utils/admin/apiRequest";
import useModal from "../../../hooks/ui/useModal";
import Pagination from "../../../components/ui/Pagination";
import { FaEdit, FaPlus, FaTrashAlt } from "react-icons/fa";
import Modal from "../../../components/ui/utils/Modal";
import Button from "../../../components/ui/utils/Button";
import useFormInput from "../../../hooks/form/useFormInput";
import { useSelector } from "react-redux";

import InputField from "../../../components/ui/form/InputField";
import DeleteVendor from "../../../components/admin/vendor/DeleteVendor";
import CreateVendorStaff from "../../../components/admin/vendor/CreateVendorStaff";
import EditVendorStaff from "../../../components/admin/vendor/EditVendorStaff";
import { getFileUrl } from "../../../utils/helper";

const VendorStaff = () => {
  const {
    currentPage,
    data,
    error,
    fetchData,
    limit,
    loading,
    setPage,
    setSorting,
    sort,
    totalItems,
    totalPages,
    setFilters,
  } = usePaginate({
    apiMethod: apiRequest.getVendorManager,
    dataKey: "vendors",
  });

  const { isOpen, openModal, closeModal, actionType } = useModal();
  const handleSort = (field) => {
    const order =
      sort.sortField === field && sort.sortOrder === "asc" ? "desc" : "asc";
    setSorting({ sortField: field, sortOrder: order });
  };



  const searchInitialValues = { code: "" };
  const { values, handleChange, setValues } = useFormInput(searchInitialValues);
  const debounceRef = useRef(null);
  useEffect(() => {
    if (values.code !== undefined) {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        const filters = {};
        filters.isSuperVendor = false
        if (values.code) {
          filters.code = { $regex: values.code, $options: "i" };
        }
        setFilters(filters);
      }, 500);
      return () => clearTimeout(debounceRef.current);
    }
  }, [values.code]);

  const BRAND_NAME = useSelector(
    (state) => state.globalState?.siteSetting?.site_settings?.site_name
  );
  return (
    <Layout>
      <Helmet>
        <title>Vendor - {BRAND_NAME ?? ''}</title>
      </Helmet>
      <div className="p-4 bg-white rounded">
        <div className="flex items-center gap-1 py-2">
          <Button
            onClick={() => openModal({ type: "add" })}
            variant="primary"
            isLoading={false}
          >
            <div className="flex items-center justify-center gap-3">
              <FaPlus />
              <p>Add New Vendor Manager</p>
            </div>
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_100px] gap-2 my-2">
          <InputField
            name="code"
            placeholder="e.g. Code"
            value={values.code}
            onChange={handleChange}
          />
          <button
            className="py-1 text-white rounded bg-emerald-600 hover:bg-emerald-800"
            onClick={() => setValues(searchInitialValues)}
          >
            Clear
          </button>
        </div>

        <Table
          headers={[
            { label: "Vendor Manager Name", key: "name", sorting: true },
            { label: "Image", key: "image", sorting: true },
            { label: "User ID", key: "userid", sorting: true },
            { label: "Contact Person", key: "contactPerson", sorting: true },
            { label: "Display Name", key: "displayName", sorting: true },
            { label: "City", key: "city", sorting: true },
            { label: "Mobile", key: "mobile", sorting: false },
            { label: "Email", key: "email", sorting: false },
            { label: "Status", key: "status", sorting: true },
            { label: "Created At", key: "createdAt", sorting: true },
            {
              label: "Action",
              key: "action",
              sorting: false,
            },
          ]}
          loading={loading}
          handleSort={handleSort}
          sort={sort}
          error={error}
          cols={7}
          renderRow={
            <tbody className="bg-white divide-y divide-gray-200">
              {data?.map((vendor) => {
                return (
                  <tr key={vendor._id} className="transition-colors hover:bg-gray-50">
                    <td className="px-3 py-3">
                      <span className="font-semibold text-gray-800">{vendor.name}</span>
                    </td>
                    <td className="px-3 py-3">
                      <img src={getFileUrl(vendor.logo)} className="object-contain w-20 h-20 border border-gray-300 rounded" alt={vendor.name} />
                    </td>
                    <td className="px-3 py-3">
                      <span className="font-semibold text-gray-800">{vendor.userId}</span>
                    </td>

                    <td className="px-3 py-3">
                      <span className="font-semibold text-gray-800">{vendor.contactPerson}</span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="font-semibold text-gray-800">{vendor.displayName}</span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-xs text-gray-500">{vendor?.cityId?.name}</span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-xs text-gray-500">{vendor.mobile}</span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-xs text-gray-500">{vendor.email || "-"}</span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-xs text-gray-500">{vendor.vendorType || "-"}</span>
                    </td>

                    <td className="px-3 py-3">
                      <span className="text-xs text-gray-400">
                        {new Date(vendor.createdAt).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex justify-center gap-3">
                        <button
                          onClick={() => openModal({ type: "edit", data: { state: vendor } })}
                          className="text-primary"
                          title="Edit"
                        >
                          <FaEdit size={18} />
                        </button>
                        <button
                          onClick={() => openModal({ type: "delete", data: { state: vendor } })}
                          className="text-red-500"
                          title="Delete"
                        >
                          <FaTrashAlt size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          }
        />
        {totalPages > 1 && (
          <Pagination
            pagination={{
              currentPage,
              totalPages,
              totalItems: totalItems,
              perPage: limit,
            }}
            handlePageChange={setPage}
            loading={loading}
          />
        )}
      </div>
      <Modal isOpen={isOpen} onClose={closeModal} size="extralarge">
        {actionType?.type === "add" && (
          <CreateVendorStaff closeModal={closeModal} callback={fetchData} />
        )}
        {actionType?.type === "edit" && (
          <EditVendorStaff
            data={actionType?.data?.state}
            closeModal={closeModal}
            callback={fetchData}
          />
        )}
        {actionType?.type === "delete" && (
          <DeleteVendor
            data={actionType?.data?.state}
            closeModal={closeModal}
            callback={fetchData}
          />
        )}
      </Modal>
    </Layout>
  );
};

export default VendorStaff;
